<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useAlert } from 'dashboard/composables';
import KraftchatAIAPI from 'dashboard/api/kraftchatAI';

import {
  formatChannelType,
  formatProviderName,
} from '../../helpers/kraftchatAIFormatters';
import {
  getOverviewNextStep,
  overviewContent,
} from '../../helpers/kraftchatAIOverviewConfig';
import KraftchatAIOverviewCard from './KraftchatAIOverviewCard.vue';
import KraftchatAIOverviewNextStep from './KraftchatAIOverviewNextStep.vue';

const route = useRoute();
const router = useRouter();

const providerCredentials = ref([]);
const aiAgents = ref([]);
const knowledgeMeta = ref({
  totalCount: 0,
  activeCount: 0,
  perPage: 5,
});
const isLoading = ref(false);

const firstAgent = computed(() => aiAgents.value[0]);

const providerCount = computed(() => providerCredentials.value.length);
const hasAgent = computed(() => Boolean(firstAgent.value));
const agentEnabled = computed(() => Boolean(firstAgent.value?.enabled));
const totalKnowledgeCount = computed(() => knowledgeMeta.value.totalCount || 0);
const activeKnowledgeCount = computed(
  () => knowledgeMeta.value.activeCount || 0
);

const providerDescription = computed(() => {
  if (!providerCredentials.value.length) {
    return overviewContent.providerEmpty;
  }

  return providerCredentials.value
    .map(provider => formatProviderName(provider.provider))
    .join(', ');
});

const agentValue = computed(() => {
  if (!firstAgent.value) {
    return overviewContent.agentMissing;
  }

  return firstAgent.value.enabled
    ? overviewContent.agentActive
    : overviewContent.agentInactive;
});

const agentDescription = computed(() => {
  if (!firstAgent.value) {
    return overviewContent.agentMissingDescription;
  }

  return `${firstAgent.value.name} · ${firstAgent.value.inbox_name}`;
});

const overviewCards = computed(() => [
  {
    icon: 'i-lucide-key-round',
    title: overviewContent.providerTitle,
    value: String(providerCount.value),
    description: providerDescription.value,
  },
  {
    icon: 'i-lucide-bot',
    title: overviewContent.agentTitle,
    value: agentValue.value,
    description: agentDescription.value,
  },
  {
    icon: 'i-lucide-message-circle',
    title: overviewContent.channelTitle,
    value: formatChannelType(firstAgent.value?.channel_type),
    description: overviewContent.channelDescription,
  },
  {
    icon: 'i-lucide-book-open',
    title: overviewContent.knowledgeTitle,
    value: `${activeKnowledgeCount.value}/${totalKnowledgeCount.value}`,
    description: overviewContent.knowledgeDescription,
  },
]);

const nextStep = computed(() =>
  getOverviewNextStep({
    providerCount: providerCount.value,
    hasAgent: hasAgent.value,
    agentEnabled: agentEnabled.value,
    activeKnowledgeCount: activeKnowledgeCount.value,
  })
);

const loadOverview = async () => {
  isLoading.value = true;

  try {
    const [credentialsResponse, agentsResponse, knowledgeResponse] =
      await Promise.all([
        KraftchatAIAPI.getProviderCredentials(),
        KraftchatAIAPI.getAgents(),
        KraftchatAIAPI.getKnowledgeDocuments({ page: 1 }),
      ]);

    providerCredentials.value = credentialsResponse.data.payload || [];
    aiAgents.value = agentsResponse.data.payload || [];
    knowledgeMeta.value = {
      totalCount: knowledgeResponse.data.meta?.totalCount || 0,
      activeCount: knowledgeResponse.data.meta?.activeCount || 0,
      perPage: knowledgeResponse.data.meta?.perPage || 5,
    };
  } catch (error) {
    useAlert(
      error.response?.data?.message ||
        error.message ||
        overviewContent.loadError
    );
  } finally {
    isLoading.value = false;
  }
};

const handleNavigate = step => {
  router.push({
    name: step.routeName,
    params: route.params,
  });
};

onMounted(loadOverview);
</script>

<template>
  <section class="space-y-6">
    <p
      v-if="isLoading"
      class="rounded-xl border border-dashed border-n-weak bg-n-solid-1 p-4 text-sm text-n-slate-11"
    >
      {{ overviewContent.loadingLabel }}
    </p>

    <section class="grid items-start gap-3 md:grid-cols-2 xl:grid-cols-4">
      <KraftchatAIOverviewCard
        v-for="card in overviewCards"
        :key="card.title"
        :card="card"
      />
    </section>

    <KraftchatAIOverviewNextStep :step="nextStep" @navigate="handleNavigate" />
  </section>
</template>
